import type {
  AttributeCollectionId,
  FieldValidationError,
  FieldValue,
} from "../types";
import { parseFieldId } from "./field-id";
import { extractValueForSubmission } from "./value-label-utils";

/**
 * コレクション関連のフィールド操作ユーティリティ
 *
 * 主な責務:
 * - コレクションフィールドの削除・フィルタリング
 * - コレクションエラーの削除
 * - 空値のフィルタリング
 */

/**
 * 特定のコレクションに属するフィールドを値配列から削除する
 *
 * 動的コレクションでminCount=0のとき「入力しない」を選択した場合に、
 * 関連するフィールドを完全に削除するために使用します。
 *
 * @param values フィールド値の配列
 * @param collectionId コレクションID
 * @param collectionIndex コレクションインデックス
 * @returns 指定したコレクションのフィールドを除外した新しい値配列
 */
export function removeCollectionFields(
  values: FieldValue[],
  collectionId: AttributeCollectionId,
  collectionIndex: number,
): FieldValue[] {
  return values.filter((fieldValue) => {
    const parsedId = parseFieldId(fieldValue.fieldId);
    // このコレクションに属さないフィールドのみ残す
    return !(
      parsedId &&
      parsedId.collectionId === collectionId &&
      parsedId.collectionIndex === collectionIndex
    );
  });
}

/**
 * フィールド値を送信用形式に変換し、空の値を除外する
 *
 * フォーム送信時に以下の処理を行います：
 * 1. {value, label}オブジェクトから値部分のみを抽出
 * 2. 空の値を持つフィールドを除外（ただし空配列は保持）
 *
 * @param values フィールド値の配列
 * @returns 送信用に変換され、空でない値を持つフィールドのみを含む新しい配列
 */
export function prepareFieldValuesForSubmission(
  values: FieldValue[],
): FieldValue[] {
  return values
    .map((field) => {
      // {value, label}オブジェクトから値部分を抽出（その他の型はそのまま保持）
      const extractedValue = extractValueForSubmission(field.value);

      return {
        ...field,
        value: extractedValue,
      };
    })
    .filter((field) => {
      const value = field.value;

      // 通常の空値チェック (ただし空配列は保持)
      const isEmpty =
        value === "" ||
        value === null ||
        value === undefined ||
        (typeof value === "object" &&
          value !== null &&
          !Array.isArray(value) && // 空配列はフィルタリングしない
          Object.keys(value).length === 0);

      // 空でない値のみを含める
      return !isEmpty;
    });
}

/**
 * 特定のコレクションに属するエラーを除外する
 *
 * コレクションを無効化または削除する際に、
 * 関連するフィールドのエラーも削除するために使用します。
 *
 * @param errors エラーの配列
 * @param collectionId コレクションID
 * @param collectionIndex コレクションインデックス
 * @returns 指定したコレクションのエラーを除外した新しい配列
 */
export function removeCollectionErrors(
  errors: FieldValidationError[],
  collectionId: AttributeCollectionId,
  collectionIndex: number,
): FieldValidationError[] {
  return errors.filter((error) => {
    const parsedId = parseFieldId(error.fieldId);
    return !(
      parsedId &&
      parsedId.collectionId === collectionId &&
      parsedId.collectionIndex === collectionIndex
    );
  });
}
